import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { ApprovalItem } from "@/types/approval";
import type { DraftPreview } from "@/types/draft";

const STALE_THRESHOLD_MS = 3 * 60 * 1000; // GEN 判断 #3: 3 分閾値
const PREVIEW_TRUNCATE = 400;

interface Props {
  item: ApprovalItem | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; preview: DraftPreview }
  | { kind: "error"; message: string };

export function SendConfirmModal({ item, submitting, onClose, onSubmit }: Props) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [confirmed, setConfirmed] = useState(false);
  const [showFullBody, setShowFullBody] = useState(false);

  useEffect(() => {
    if (!item) return;
    setLoad({ kind: "loading" });
    setConfirmed(false);
    setShowFullBody(false);
    api
      .draftPreview(item.id)
      .then((p) => setLoad({ kind: "loaded", preview: p }))
      .catch((err: Error) => setLoad({ kind: "error", message: err.message }));
  }, [item]);

  if (!item) return null;

  const preview = load.kind === "loaded" ? load.preview : null;
  const now = Date.now();
  const isStale =
    preview && preview.updatedAt && now - preview.updatedAt < STALE_THRESHOLD_MS;
  const hasDisallowed = preview && preview.disallowedRecipients.length > 0;
  const sendDisabled =
    !preview ||
    !preview.sendEnabled ||
    hasDisallowed ||
    !confirmed ||
    submitting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-border bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold">✉️ 送信内容の確認</h3>
        <p className="mt-1 text-xs text-muted-foreground truncate">
          {item.title || "(無題)"}
        </p>

        {load.kind === "loading" && (
          <p className="mt-4 text-sm text-muted-foreground">draft 読み込み中…</p>
        )}

        {load.kind === "error" && (
          <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">プレビュー取得失敗</p>
            <p className="text-xs text-muted-foreground mt-1 break-all">{load.message}</p>
          </div>
        )}

        {preview && (
          <div className="mt-3 space-y-3 text-sm">
            {!preview.sendEnabled && (
              <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
                <p className="font-medium text-yellow-600 dark:text-yellow-400">
                  ⚠️ 送信機能 disabled
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Worker の `ENABLE_GMAIL_SEND` が false。送信ボタンは押せません。
                </p>
              </div>
            )}

            {hasDisallowed && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <p className="font-medium text-destructive">
                  🚫 allowlist 外の送信先
                </p>
                <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                  {preview.disallowedRecipients.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  draft を Gmail で開いて、to/cc/bcc を社内向けに修正してください。
                </p>
              </div>
            )}

            {isStale && (
              <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
                <p className="font-medium text-yellow-600 dark:text-yellow-400">
                  ⏱️ 直近 3 分以内に編集された draft
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  内容が想定と一致しているか再確認推奨。
                </p>
              </div>
            )}

            <Row label="To">
              <AddrList list={preview.to} disallow={preview.disallowedRecipients} />
            </Row>
            {preview.cc.length > 0 && (
              <Row label="Cc">
                <AddrList list={preview.cc} disallow={preview.disallowedRecipients} />
              </Row>
            )}
            {preview.bcc.length > 0 && (
              <Row label="Bcc">
                <AddrList list={preview.bcc} disallow={preview.disallowedRecipients} />
              </Row>
            )}
            <Row label="Subject">
              <p className="break-words">{preview.subject || "(no subject)"}</p>
            </Row>
            <Row label="Body">
              <pre className="whitespace-pre-wrap break-words rounded bg-secondary p-2 text-xs leading-relaxed text-secondary-foreground max-h-64 overflow-y-auto">
                {showFullBody || preview.bodyText.length <= PREVIEW_TRUNCATE
                  ? preview.bodyText
                  : preview.bodyText.slice(0, PREVIEW_TRUNCATE) + "…"}
              </pre>
              {preview.bodyText.length > PREVIEW_TRUNCATE && (
                <button
                  onClick={() => setShowFullBody((v) => !v)}
                  className="mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  {showFullBody ? "折りたたむ" : `全文表示（${preview.bodyText.length} 字）`}
                </button>
              )}
            </Row>

            <label className="flex items-start gap-2 cursor-pointer select-none mt-2">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={submitting}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong>内容を確認した</strong>
                <span className="text-muted-foreground">
                  {" "}（送信先 / 件名 / 本文を読みました）
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-4 flex gap-2 justify-end sticky bottom-0 bg-card pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-3 py-2 text-sm text-muted-foreground active:opacity-80 disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={sendDisabled}
            className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium active:opacity-80 disabled:opacity-50"
          >
            {submitting ? "送信中…" : "✉️ 送信して承認"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function AddrList({ list, disallow }: { list: string[]; disallow: string[] }) {
  const bad = new Set(disallow);
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((a) => (
        <span
          key={a}
          className={
            "rounded px-1.5 py-0.5 text-xs " +
            (bad.has(a)
              ? "bg-destructive/20 text-destructive-foreground border border-destructive/50"
              : "bg-secondary text-secondary-foreground")
          }
        >
          {a}
        </span>
      ))}
    </div>
  );
}
